// Admin overview (/admin) — business metrics (docs/API.md §8). One-time model:
// revenue = frozen paid-order sums; NO MRR/renewal/subscription framing. Sales
// are closed (DECISIONS D36): order and revenue figures are history, shown
// without any sales prompt. The MCP relay group (D45) renders only when the API
// sends `metrics.relay`: identity-free aggregates, never a call's content (D42).

import type { ReactNode } from "react";
import { headingClass } from "@ahcut/ui/recipes";
import { PageHeader } from "@/components/PageHeader";
import { Loading, ErrorState } from "@/components/States";
import { StatCard } from "@/components/StatCard";
import { cn } from "@/lib/cn";
import { useAsync } from "@/lib/useAsync";
import { getAdminMetrics } from "@/lib/admin";
import {
  formatBytes,
  formatDate,
  formatDurationMs,
  formatPercent,
  formatRupiah,
  tierLabel,
} from "@/lib/format";
import type { AdminMetrics, AdminRelayDay, AdminRelayStats } from "@/lib/types";
import { AdminPanel } from "@/components/admin/AdminPanel";
import {
  DataCell,
  DataEmptyRow,
  DataRow,
  DataTable,
  NoValue,
  type DataColumn,
} from "@/components/admin/DataTable";
import { dataCellClass } from "@/components/admin/classes";

const SALES_COLUMNS: readonly DataColumn[] = [
  { label: "Tier" },
  { label: "Pesanan lunas", align: "end" },
  { label: "Pendapatan", align: "end" },
];

const LICENSE_COLUMNS: readonly DataColumn[] = [
  { label: "Tier" },
  { label: "Aktif", align: "end" },
  { label: "Terikat perangkat", align: "end" },
  { label: "Dicabut", align: "end" },
];

const RELAY_DAY_COLUMNS: readonly DataColumn[] = [
  { label: "Tanggal" },
  { label: "Panggilan", align: "end" },
  { label: "Gagal", align: "end" },
  { label: "Habis waktu", align: "end" },
  { label: "Dibatalkan", align: "end" },
  { label: "Rata-rata", align: "end" },
];

const RELAY_TOOL_COLUMNS: readonly DataColumn[] = [
  { label: "Tool" },
  { label: "Panggilan", align: "end" },
  { label: "Gagal", align: "end" },
];

/** Rows in the relay's per-day table, and in its tool table. */
const RELAY_RECENT_DAYS = 7;
const RELAY_TOP_TOOLS = 5;

/** The entries of `days` (newest first) dated within the last `count` UTC days —
 *  the API keys its days in UTC and sends only days that had calls. Never
 *  zero-filled: a day with no entry shows no row, rather than a 0 nobody sent. */
function recentRelayDays(days: readonly AdminRelayDay[], count: number): AdminRelayDay[] {
  const cutoff = new Date(Date.now() - (count - 1) * 86_400_000).toISOString().slice(0, 10);
  return days.filter((d) => d.day >= cutoff).slice(0, count);
}

/** A titled group of metrics: an h2 in the display face (DESIGN.md §4 heading
 *  role, under the 30px page title), StatCards in two columns from 640px. */
function MetricGroup({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className="flex min-w-0 flex-col gap-4">
      <h2 id={id} className={headingClass}>
        {title}
      </h2>
      {children}
    </section>
  );
}

export function AdminOverview() {
  const { data: m, loading, error, reload } = useAsync<AdminMetrics>(
    (s) => getAdminMetrics(s),
    [],
  );

  if (loading) return <Loading label="Memuat metrik…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!m) return <ErrorState error={new Error("Metrik tidak tersedia.")} onRetry={reload} />;

  const rate = formatPercent(m.conversion.rate);

  return (
    <div className="flex min-w-0 flex-col gap-10">
      <PageHeader
        title="Ringkasan"
        lede="Metrik pengguna, lisensi, dan unduhan ahcut. Angka pesanan dan pendapatan adalah riwayat; penjualan sudah ditutup."
      />

      <MetricGroup id="ringkasan-pengguna" title="Pengguna">
        <div className="grid gap-4 sm:grid-cols-2">
          <StatCard
            label="Total pengguna"
            value={m.users.total}
            sub={`+${m.users.new_7d} (7 hari) · +${m.users.new_30d} (30 hari)`}
          />
          <StatCard
            label="Signup via referral"
            value={m.users.referred}
            sub="kode referral tercatat saat daftar"
          />
          <div className="sm:col-span-2">
            <StatCard
              label="Unduhan (unik per hari)"
              value={m.downloads ? m.downloads.total.uniques : "—"}
              sub={
                m.downloads
                  ? `${m.downloads.total.hits} redirect · Windows ${m.downloads.windows.uniques} · macOS ${m.downloads.macos.uniques}` +
                    (m.downloads.since ? ` · sejak ${formatDate(m.downloads.since)}` : "")
                  : "Penghitung edge belum dikonfigurasi (DOWNLOAD_STATS_TOKEN)"
              }
            />
          </div>
        </div>
      </MetricGroup>

      <MetricGroup id="ringkasan-lisensi" title="Lisensi">
        <div className="grid gap-4 sm:grid-cols-2">
          <StatCard
            label="Lisensi berbayar aktif"
            value={m.licenses.active_paid}
            sub={`${m.licenses.activated} terikat perangkat · ${m.licenses.revoked} dicabut`}
          />
          <StatCard
            label="Pengguna berbayar"
            value={m.conversion.paid_users}
            sub={`Konversi ${rate}`}
          />
        </div>
        <AdminPanel title="Lisensi per tier" titleAs="h3">
          <DataTable label="Lisensi per tier" columns={LICENSE_COLUMNS} stackBelow="40rem">
            {m.licenses.by_tier.length === 0 ? (
              <DataEmptyRow colSpan={LICENSE_COLUMNS.length}>Belum ada lisensi.</DataEmptyRow>
            ) : (
              m.licenses.by_tier.map((t) => (
                <DataRow key={t.tier}>
                  <DataCell label="Tier" className="text-fg-0">
                    {tierLabel(t.tier)}
                  </DataCell>
                  <DataCell label="Aktif" align="end" className={dataCellClass}>
                    {t.active}
                  </DataCell>
                  <DataCell label="Terikat perangkat" align="end" className={dataCellClass}>
                    {t.activated}
                  </DataCell>
                  <DataCell label="Dicabut" align="end" className={dataCellClass}>
                    {t.revoked}
                  </DataCell>
                </DataRow>
              ))
            )}
          </DataTable>
        </AdminPanel>
      </MetricGroup>

      <MetricGroup id="ringkasan-pesanan" title="Pesanan">
        <div className="grid gap-4 sm:grid-cols-2">
          <StatCard
            label="Pendapatan"
            value={formatRupiah(m.orders.revenue_total)}
            sub={`${m.orders.paid} pesanan lunas`}
          />
          <StatCard
            label="Pesanan menunggu"
            value={m.orders.pending}
            sub={`${m.orders.failed} gagal`}
          />
          <div className="sm:col-span-2">
            <StatCard
              label="Webhook bermasalah"
              value={m.webhooks.problem_7d}
              sub="7 hari terakhir (tak cocok / jumlah beda)"
            />
          </div>
        </div>
        <AdminPanel title="Penjualan per tier" titleAs="h3">
          <DataTable label="Penjualan per tier" columns={SALES_COLUMNS} stackBelow="40rem">
            {m.orders.by_tier.length === 0 ? (
              <DataEmptyRow colSpan={SALES_COLUMNS.length}>Belum ada penjualan.</DataEmptyRow>
            ) : (
              m.orders.by_tier.map((t) => (
                <DataRow key={t.tier}>
                  <DataCell label="Tier" className="text-fg-0">
                    {tierLabel(t.tier)}
                  </DataCell>
                  <DataCell label="Pesanan lunas" align="end" className={dataCellClass}>
                    {t.paid_count}
                  </DataCell>
                  <DataCell label="Pendapatan" align="end" className={dataCellClass}>
                    {formatRupiah(t.revenue)}
                  </DataCell>
                </DataRow>
              ))
            )}
          </DataTable>
        </AdminPanel>
      </MetricGroup>

      {m.relay && <RelayMetrics relay={m.relay} />}
    </div>
  );
}

/** The MCP relay over its window (DECISIONS D42 (3), D45): totals, the last days
 *  and the busiest tools. Identity-free — no user, client or argument. */
function RelayMetrics({ relay }: { relay: AdminRelayStats }) {
  const totals = relay.days.reduce(
    (sum, d) => ({
      calls: sum.calls + d.calls,
      errors: sum.errors + d.errors,
      timeouts: sum.timeouts + d.timeouts,
      cancelled: sum.cancelled + d.cancelled,
      reqBytes: sum.reqBytes + d.req_bytes,
      respBytes: sum.respBytes + d.resp_bytes,
      // avg_ms is per day; weighting it by that day's calls gives the window's mean.
      weightedMs: sum.weightedMs + d.avg_ms * d.calls,
    }),
    { calls: 0, errors: 0, timeouts: 0, cancelled: 0, reqBytes: 0, respBytes: 0, weightedMs: 0 },
  );
  const span = `${relay.window_days} hari`;
  const recent = recentRelayDays(relay.days, RELAY_RECENT_DAYS);
  const topTools = relay.tools.slice(0, RELAY_TOP_TOOLS);

  return (
    <MetricGroup id="ringkasan-relay" title="Relay MCP">
      <div className="grid gap-4 sm:grid-cols-2">
        <StatCard
          label={`Panggilan (${span})`}
          value={totals.calls}
          // A timed-out or abandoned call also counts as failed, so the last two are
          // part of the first, never added to it.
          sub={`${totals.errors} gagal, termasuk ${totals.timeouts} habis waktu dan ${totals.cancelled} dibatalkan`}
        />
        <StatCard
          label={`Durasi rata-rata (${span})`}
          value={totals.calls > 0 ? formatDurationMs(totals.weightedMs / totals.calls) : "—"}
          sub="per panggilan"
        />
        <div className="sm:col-span-2">
          <StatCard
            label={`Data lewat relay (${span})`}
            value={formatBytes(totals.reqBytes + totals.respBytes)}
            sub={`permintaan ${formatBytes(totals.reqBytes)} · jawaban ${formatBytes(totals.respBytes)}`}
          />
        </div>
      </div>
      <AdminPanel title={`${RELAY_RECENT_DAYS} hari terakhir`} titleAs="h3">
        <DataTable label="Panggilan relay per hari" columns={RELAY_DAY_COLUMNS} stackBelow="58rem">
          {recent.length === 0 ? (
            <DataEmptyRow colSpan={RELAY_DAY_COLUMNS.length}>
              Tidak ada panggilan tercatat dalam {RELAY_RECENT_DAYS} hari terakhir.
            </DataEmptyRow>
          ) : (
            recent.map((d) => (
              <DataRow key={d.day}>
                <DataCell label="Tanggal" className={dataCellClass}>
                  {/* Local midnight, so the calendar date prints as sent in any time zone. */}
                  {formatDate(`${d.day}T00:00:00`)}
                </DataCell>
                <DataCell label="Panggilan" align="end" className={dataCellClass}>
                  {d.calls}
                </DataCell>
                <DataCell label="Gagal" align="end" className={dataCellClass}>
                  {d.errors}
                </DataCell>
                <DataCell label="Habis waktu" align="end" className={dataCellClass}>
                  {d.timeouts}
                </DataCell>
                <DataCell label="Dibatalkan" align="end" className={dataCellClass}>
                  {d.cancelled}
                </DataCell>
                <DataCell label="Rata-rata" align="end" className={dataCellClass}>
                  {d.calls > 0 ? formatDurationMs(d.avg_ms) : <NoValue label="Tidak ada panggilan" />}
                </DataCell>
              </DataRow>
            ))
          )}
        </DataTable>
      </AdminPanel>
      <AdminPanel title="Tool yang paling sering dipanggil" titleAs="h3">
        <DataTable label="Tool yang paling sering dipanggil" columns={RELAY_TOOL_COLUMNS} stackBelow="40rem">
          {topTools.length === 0 ? (
            <DataEmptyRow colSpan={RELAY_TOOL_COLUMNS.length}>Belum ada panggilan tool.</DataEmptyRow>
          ) : (
            topTools.map((t) => (
              <DataRow key={t.tool}>
                <DataCell label="Tool" className={cn(dataCellClass, "text-fg-0")}>
                  {/* "" = calls that named no tool (initialize, tools/list). */}
                  {t.tool || <span className="font-sans text-fg-2">tanpa tool</span>}
                </DataCell>
                <DataCell label="Panggilan" align="end" className={dataCellClass}>
                  {t.calls}
                </DataCell>
                <DataCell label="Gagal" align="end" className={dataCellClass}>
                  {t.errors}
                </DataCell>
              </DataRow>
            ))
          )}
        </DataTable>
      </AdminPanel>
    </MetricGroup>
  );
}
